import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Loader2,
  User,
  Key,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import RoleSelect from '@/components/RoleSelect';
import { useUserDetail, useReferenceRoles } from '../services/queries';
import { useDeleteUser, useUpdateUser } from '../services/mutations';
import { toast } from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import useAuth from '../hooks/useAuth';
import FormDrawer from '../components/FormDrawer';

const UserDetail = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { checkPermissionElement } = useAuth();
  const {
    data: userDetail,
    isLoading,
    error,
    refetch,
  } = useUserDetail(username);
  const deleteUser = useDeleteUser();
  const updateUser = useUpdateUser();
  const { data: dataRoles, refetch: refetchRoles } = useReferenceRoles();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    password: '',
    email: '',
    roles: [],
    position: '',
    employee_id: '',
    credentials: [],
  });

  useEffect(() => {
    if (username) {
      refetch();
      refetchRoles();
    }
  }, [username, refetch, refetchRoles]);

  const handleBack = () => {
    navigate('/user-management');
  };

  const handleEdit = () => {
    if (userDetail?.data) {
      const { firstName, lastName } = splitFullName(userDetail.data.full_name);
      setForm({
        first_name: firstName || '',
        last_name: lastName || '',
        username: userDetail.data.username || '',
        password: '',
        email: userDetail.data.email || '',
        roles: userDetail.data.roles || [],
        position: userDetail.data.profile?.position || '',
        employee_id: userDetail.data.profile?.employee_id || '',
        credentials: userDetail.data.credentials || [],
      });
      setFormError(null);
      setIsEditOpen(true);
    }
  };

  const handleDelete = () => {
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await deleteUser.mutateAsync(username);
      toast.success('User deleted successfully', {
        className: 'toast-success',
      });
      setIsModalOpen(false);
      navigate('/user-management');
    } catch (error) {
      console.error('Error during user delete:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10 page-shell">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-shell">
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>
            {error.message || 'Failed to load user details'}
          </AlertDescription>
        </Alert>
        <Button
          onClick={handleBack}
          className="mt-2 flex items-center gap-2"
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to User Management
        </Button>
      </div>
    );
  }

  const user = userDetail?.data;

  if (!user) {
    return (
      <div className="page-shell">
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>User not found</AlertDescription>
        </Alert>
        <Button
          onClick={handleBack}
          className="mt-2 flex items-center gap-2"
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to User Management
        </Button>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const splitFullName = (fullName) => {
    const fullNameParts = fullName ? fullName.split(' ') : ['', ''];
    const firstName = fullNameParts[0];
    const lastName = fullNameParts.slice(1).join(' ');

    return { firstName, lastName };
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCredentialChange = (appCode, field, value) => {
    setForm((prev) => ({
      ...prev,
      credentials: prev.credentials.map((cred) =>
        cred.application_code === appCode ? { ...cred, [field]: value } : cred,
      ),
    }));
  };

  const handleToggleRole = (roleObj) => {
    const isSelected = form.roles.includes(roleObj.role);
    const updatedRoles = isSelected
      ? form.roles.filter((r) => r !== roleObj.role)
      : [...form.roles, roleObj.role];
    setForm((prev) => ({ ...prev, roles: updatedRoles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitLoading(true);

    const payload = { ...form, roles: form.roles || [] };
    // Remove empty credentials
    payload.credentials = (payload.credentials || []).filter(
      (cred) => cred.username && cred.password,
    );

    // Remove empty string or empty arrays
    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === '' ||
        (Array.isArray(payload[key]) && payload[key].length === 0)
      ) {
        delete payload[key];
      }
    });

    try {
      await updateUser.mutateAsync({
        username: user.username,
        body: payload,
      });
      toast.success('User updated successfully', {
        className: 'toast-success',
      });
      setIsEditOpen(false);
      refetch();
    } catch (error) {
      setFormError(error.response?.data?.detail || error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="page-header mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">User Detail</h1>
            <p className="text-muted-foreground text-sm">
              Profil, kredensial, dan akses pengguna
            </p>
          </div>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          {checkPermissionElement('superapp:user-management:button:edit') && (
            <Button
              variant="outline"
              onClick={handleEdit}
              className="flex gap-2 flex-1 md:flex-initial"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          {checkPermissionElement('superapp:user-management:button:delete') && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="flex gap-2 flex-1 md:flex-initial"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Profile Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={user.picture_url} alt={user.full_name} />
                <AvatarFallback className="text-xl bg-muted text-foreground">
                  {getInitials(user.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="text-center md:text-left space-y-1">
                <h2 className="text-2xl font-bold">{user.full_name}</h2>
                <p className="text-muted-foreground">@{user.username}</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-2 pt-2">
                  {user.profile?.position && (
                    <Badge variant="outline">{user.profile.position}</Badge>
                  )}
                  {user.profile?.employee_id && (
                    <Badge variant="secondary">
                      NIP: {user.profile.employee_id}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" /> Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Username
                  </label>
                  <p className="mt-1 font-medium">{user.username || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Full Name
                  </label>
                  <p className="mt-1 font-medium">{user.full_name || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <p className="mt-1 font-medium">{user.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    NIP
                  </label>
                  <p className="mt-1 font-medium">
                    {user.profile?.employee_id || '-'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Position
                  </label>
                  <p className="mt-1 font-medium">
                    {user.profile?.position || '-'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Applications */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ExternalLink className="h-5 w-5" /> Applications
                </CardTitle>
                <CardDescription>
                  Aplikasi yang dapat diakses oleh pengguna ini
                </CardDescription>
              </CardHeader>
              <CardContent>
                {user.applications && user.applications.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.applications.map((app) => (
                      <div
                        key={app.code}
                        className="rounded-lg border bg-card text-card-foreground shadow-sm p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => window.open(app.host, '_blank')}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold">{app.name}</h4>
                          {app.is_active && (
                            <Badge
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Active
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground font-mono mb-1">
                          {app.code}
                        </p>
                        <p className="text-xs text-blue-600 truncate">
                          {app.host}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Tidak ada aplikasi yang dikelola
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Credentials (Accordion) */}
            {user.credentials && user.credentials.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Key className="h-5 w-5" /> Credentials
                  </CardTitle>
                  <CardDescription>
                    Kredensial spesifik aplikasi untuk pengguna ini
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {user.credentials.map((cred, idx) => (
                      <AccordionItem key={idx} value={`item-${idx}`}>
                        <AccordionTrigger className="hover:no-underline py-3 px-1">
                          <span className="font-medium">
                            {cred.application_code}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="bg-muted/50 rounded-md p-3 space-y-2">
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <span className="text-muted-foreground font-medium">
                                Username:
                              </span>
                              <span className="col-span-2 font-mono">
                                {cred.username}
                              </span>
                            </div>
                            {/* Password intentionally hidden/not shown in clear text usually, maybe masked or just indicate set */}
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <span className="text-muted-foreground font-medium">
                                Password:
                              </span>
                              <span className="col-span-2 font-mono text-xs">
                                ********
                              </span>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            {/* Roles */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Roles</CardTitle>
              </CardHeader>
              <CardContent>
                {user.roles && user.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.roles.map((role) => (
                      <Badge key={role} variant="secondary">
                        {role}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No roles assigned
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Created At
                  </label>
                  <p className="text-sm font-medium">
                    {formatDate(user.created_at)}
                  </p>
                </div>
                <Separator />
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Updated At
                  </label>
                  <p className="text-sm font-medium">
                    {formatDate(user.updated_at)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <FormDrawer isOpen={isEditOpen} setIsOpen={setIsEditOpen}>
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[520px] flex flex-col h-full"
        >
          <div>
            <h3 className="text-xl font-semibold">Edit User</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Update user details, roles, and credentials.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto space-y-5">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-3">
              <h4 className="font-semibold">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChangeInput}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChangeInput}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    name="username"
                    value={form.username}
                    onChange={handleChangeInput}
                    disabled
                    className="bg-muted text-muted-foreground"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChangeInput}
                    placeholder="Leave blank to keep current password"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChangeInput}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    name="position"
                    value={form.position}
                    onChange={handleChangeInput}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="employee_id">NIP</Label>
                  <Input
                    id="employee_id"
                    name="employee_id"
                    value={form.employee_id}
                    onChange={handleChangeInput}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold">Roles</h4>
              <RoleSelect
                placeholder={
                  form.roles.length > 0
                    ? `${form.roles.length} role(s) selected`
                    : 'Select roles'
                }
                roles={dataRoles?.data || []}
                selectedRoles={form.roles}
                onToggle={handleToggleRole}
              />
              <div className="flex flex-wrap gap-2 min-h-[32px]">
                {form.roles.length > 0 ? (
                  form.roles.map((role) => {
                    const detail = dataRoles?.data?.find(
                      (r) => r.role === role,
                    );
                    return (
                      <Badge
                        key={role}
                        variant="secondary"
                        className="cursor-pointer"
                        onClick={() => handleToggleRole(detail || { role })}
                      >
                        {detail?.name || role} ×
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-sm text-muted-foreground italic">
                    No roles selected
                  </span>
                )}
              </div>
            </div>

            {form.credentials && form.credentials.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold">Credentials</h4>
                <div className="space-y-3">
                  {form.credentials.map((cred, idx) => (
                    <div
                      key={`${cred.application_code}-${idx}`}
                      className="border rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">
                            {cred.application_code}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Application credential
                          </p>
                        </div>
                        <Badge variant="outline">Credential</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Username</Label>
                          <Input
                            value={cred.username || ''}
                            onChange={(e) =>
                              handleCredentialChange(
                                cred.application_code,
                                'username',
                                e.target.value,
                              )
                            }
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Password</Label>
                          <Input
                            type="password"
                            value={cred.password || ''}
                            onChange={(e) =>
                              handleCredentialChange(
                                cred.application_code,
                                'password',
                                e.target.value,
                              )
                            }
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="pt-3 border-t bg-muted/10 flex justify-end gap-3">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setIsEditOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitLoading}>
              {submitLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update User
            </Button>
          </div>
        </form>
      </FormDrawer>

      <ConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        description={`Are you sure you want to delete user "${user.full_name}"?`}
        loading={loading}
      />
    </div>
  );
};

export default UserDetail;
